/**
 * EventAttendance — R5 (2026-05-17).
 *
 * Same data source as EventsByOrganization but presented attendance-first:
 * attendance count, verified member count, and close rate are the primary
 * columns. Designed for sales managers who want to scan operational hit
 * rates without all the contextual columns.
 *
 * Excludes draft + cancelled events from the visible default since neither
 * contributes to attendance metrics. Operator can override via status filter.
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getEventsForOrganizations,
  getEventCloseRateStatsBulk,
  indexCloseRateStatsByEventId,
  type EventReportRow,
  type ReportEventsFilter,
} from "../../services/reportsSuite.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";
import VMCutoverBanner from "../../components/reports/VMCutoverBanner";

const PAGE_SIZE = 50;

const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
): ReportEventsFilter => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  // Default to non-draft/non-cancelled if operator hasn't picked a status;
  // operator can override by selecting a specific status.
  status: f.status || undefined,
  type: f.type,
  state: f.state,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  page,
  limit,
});

const EventAttendance: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.state,
    filters.type,
    filters.status,
    filters.orgId,
  ]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-event-attendance", filters, page],
    queryFn: () =>
      getEventsForOrganizations(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const eventIds = useMemo(() => rows.map((r: any) => r.id), [rows]);
  const { data: statsRows } = useQuery({
    queryKey: ["event-attendance-stats", eventIds],
    queryFn: () => getEventCloseRateStatsBulk(eventIds),
    enabled: eventIds.length > 0,
  });
  const statsByEvent = useMemo(
    () => indexCloseRateStatsByEventId(statsRows ?? []),
    [statsRows],
  );

  const EXPORT_COLUMNS = [
    "Event ID",
    "Event Name",
    "Event Date",
    "Type",
    "Status",
    "State",
    "Location",
    "Attendance",
    "Verified Members",
    "Close Rate %",
  ];

  // Post-ship audit must-fix #2: bulk-hydrate close-rate stats against
  // pageRows (all exported rows), not the visible-page cache.
  const chunk = <T,>(arr: T[], size: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }
    return out;
  };

  const mapRows = async (
    pageRows: EventReportRow[],
  ): Promise<Record<string, unknown>[]> => {
    const eventIds = pageRows.map((r) => r.id).filter(Boolean);
    const statsChunks = await Promise.all(
      chunk(eventIds, 5000).map((c) => getEventCloseRateStatsBulk(c)),
    );
    const exportStatsByEvent = indexCloseRateStatsByEventId(statsChunks.flat());
    return pageRows.map((r) => {
      const s = exportStatsByEvent.get(r.id);
      return {
        "Event ID": r.id,
        "Event Name": r.title,
        "Event Date": formatDate(r.start_time),
        Type: r.type,
        Status: r.status,
        State: r.location_state || r.state || "",
        Location: r.location_name || r.location_city || "",
        Attendance: s?.attendance_count ?? r.attendance_count ?? "",
        "Verified Members": s?.verified_member_count ?? "",
        "Close Rate %":
          s?.close_rate !== null && s?.close_rate !== undefined
            ? (s.close_rate * 100).toFixed(2)
            : "",
      };
    });
  };

  return (
    <div className="space-y-4">
      <VMCutoverBanner />
      <ReportPageHeader
        title="Event Attendance & Close Rate"
        description={
          <>
            Per-event attendance, verified members issued, and close rate.
            "Verified" = unique contacts issued a voucher tied to the event.
          </>
        }
        actions={
          <ExportButton
            filters={filters}
            fetchPage={(f, p, l) =>
              getEventsForOrganizations(filterToRpc(f, p, l)).then((res) => ({
                rows: res.rows,
                total: res.total,
              }))
            }
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="event-attendance"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showStatus={true}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2 text-right">Attendance</th>
              <th className="px-3 py-2 text-right">Verified</th>
              <th className="px-3 py-2 text-right">Close Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  No matching events.
                </td>
              </tr>
            )}
            {rows.map((r: any) => {
              const s = statsByEvent.get(r.id);
              return (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <Link
                      to={`/events/${r.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {r.title}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{formatDate(r.start_time)}</td>
                  <td className="px-3 py-2">{r.type}</td>
                  <td className="px-3 py-2">
                    {r.location_state || r.state || ""}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.attendance_count ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {s?.verified_member_count ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {s?.close_rate !== null && s?.close_rate !== undefined
                      ? `${(s.close_rate * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventAttendance;
